import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, shareReplay, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Api } from '../api-common-service/api';
import { HelperService } from '../api-common-service/helper.service';
import { CommonConstants } from '../constants/common-constants';

interface RefreshTokenResponse {
  meta?: { status?: number; message?: string };
  data?: { token?: string; refreshToken?: string };
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly refreshHttp: HttpClient;
  private refreshRequest$?: Observable<RefreshTokenResponse>;

  constructor(
    backend: HttpBackend,
    private readonly helper: HelperService,
    private readonly router: Router,
    private readonly api: Api,
  ) {
    // Bypass interceptors so a failed refresh request cannot recurse.
    this.refreshHttp = new HttpClient(backend);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const accessToken = this.helper.getLocalStorageData(CommonConstants.TOKEN);
    const authenticatedRequest = accessToken ? this.withToken(request, accessToken) : request;

    return next.handle(authenticatedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.isUnauthorized(error) || this.isRefreshRequest(request)) {
          return throwError(() => error);
        }

        const refreshToken = this.helper.getLocalStorageData(CommonConstants.REFRESH_TOKEN);
        if (!refreshToken) {
          this.endSession();
          return throwError(() => error);
        }

        return this.refreshAccessToken(refreshToken).pipe(
          switchMap(response => {
            const newAccessToken = response?.data?.token;
            const newRefreshToken = response?.data?.refreshToken;

            if (response?.meta?.status !== 1 || !newAccessToken || !newRefreshToken) {
              this.endSession();
              return throwError(() => error);
            }

            this.helper.setLocalStorageData(CommonConstants.TOKEN, newAccessToken);
            this.helper.setLocalStorageData(CommonConstants.REFRESH_TOKEN, newRefreshToken);
            return next.handle(this.withToken(request, newAccessToken));
          }),
          catchError(refreshError => {
            this.endSession();
            return throwError(() => refreshError);
          }),
        );
      }),
    );
  }

  private refreshAccessToken(refreshToken: string): Observable<RefreshTokenResponse> {
    if (!this.refreshRequest$) {
      const endpoint = environment.baseUrl + this.api.API.AUTH.REFRESH_TOKEN;
      this.refreshRequest$ = this.refreshHttp.post<RefreshTokenResponse>(endpoint, { refreshToken }).pipe(
        finalize(() => (this.refreshRequest$ = undefined)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.refreshRequest$;
  }

  private withToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private isUnauthorized(error: HttpErrorResponse): boolean {
    return error.status === 401 || error.error?.statusCode === 401;
  }

  private isRefreshRequest(request: HttpRequest<unknown>): boolean {
    return request.url.includes(this.api.API.AUTH.REFRESH_TOKEN);
  }

  private endSession(): void {
    this.helper.clearLocalStorageData();
    this.router.navigateByUrl('/login');
  }
}
